import Image from 'next/image';
import { useEffect, useRef } from 'react';
import Button from '@/components/homepage/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import RichText from '@/components/RichText/RichText';
import { subHeadersStyles } from '@/consts/builder';
import useMediaQuery from '@/hooks/useMediaQuery';
import { ActionsType } from '@/store/types/IBuilder';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId, getComponentMargin, getComponentOutlineMargin, isRichTextEmpty } from '@/utils/builderUtils';

interface DoubleImage {
  actions: ActionsType[];
  altText: string;
  copyColor: string;
  description: string;
  href: string;
  mobileSrc: string;
  src: string;
  title: string;
  textBoxPosition: string;
}

type DoubleImageBannerProps = {
  alignmentOptions: string;
  anchorLinkID: string;
  image1?: DoubleImage;
  image2?: DoubleImage;
  outlineMargin: boolean;
  margin: boolean;
  textPosition: string;
};

function ImagesText({
  alignmentOptions,
  buttonRefs,
  btnIndex,
  imageInfo,
  isDesktop,
  isFirstImage = true,
  slicedNumber = 1,
}: {
  alignmentOptions: string;
  buttonRefs: any;
  btnIndex?: number;
  imageInfo: DoubleImage;
  isDesktop: boolean;
  isFirstImage?: boolean;
  slicedNumber?: number;
}) {
  const { actions, copyColor, description, title } = imageInfo;
  const isTextWhite = copyColor === 'white';
  const HeaderTag = isFirstImage ? 'h1' : 'h2';
  const slicedActions: ActionsType[] =
    actions?.slice(0, slicedNumber).map(action => ({
      ...action,
      color: (action.color as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
      mobileColor: (action.mobileColor as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
    })) || [];
  const isBoxCenter = (!isDesktop && slicedNumber === 2) || alignmentOptions === 'center';
  return (
    <div className="tw-absolute tw-pointer-events-none tw-top-0 tw-right-0 tw-bottom-0 tw-left-0 tw-w-full tw-flex tw-items-center tw-justify-center">
      <div
        className={beTwMerge(
          'tw-flex tw-h-full tw-flex-col tw-justify-end tw-items-center tw-px-6 tw-py-6 large:tw-px-12 large:tw-py-12',
          isBoxCenter && 'tw-justify-center',
          slicedNumber === 2 && 'large:tw-w-[50%]'
        )}
      >
        <HeaderTag
          className={`tw-mb-4 tw-text-center ${subHeadersStyles['h1']} ${isTextWhite ? 'tw-text-color-white' : ''}`}
        >
          {title}
        </HeaderTag>
        {!isRichTextEmpty(description) && (
          <RichText
            className={`tw-text-center tw-pointer-events-auto tw-mb-4 large:tw-mb-8 ${isTextWhite ? 'tw-text-color-white' : ''}`}
            content={description}
            isOnlyPcTextWhite={false}
            isTextWhite={isTextWhite}
          />
        )}
        {slicedActions.length > 0 && (
          <div className="tw-pointer-events-auto tw-flex tw-w-full tw-justify-center tw-gap-[8px]">
            {slicedActions.map((action, i) => {
              const { color, isDropdownButton, href, label, mobileColor, mobileVariant, variant } = action;
              if (isDropdownButton && action.actions?.length) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    btnRef={(el: any) => {
                      buttonRefs.current[btnIndex !== undefined ? btnIndex : i] = el;
                    }}
                    className="tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full tw-text-center [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis"
                    color={isDesktop ? color : mobileColor}
                    key={i}
                    label={label}
                    options={subActions}
                    type={isDesktop ? variant : mobileVariant}
                  />
                );
              }
              return (
                <Button
                  className="tw-min-w-[146px] tw-font-avenirmedium tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden"
                  color={isDesktop ? color : mobileColor}
                  href={href}
                  key={i}
                  label={label}
                  ref={(el: any) => {
                    buttonRefs.current[btnIndex !== undefined ? btnIndex : i] = el;
                  }}
                  type={isDesktop ? variant : mobileVariant}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function HeroBanner({
  alignmentOptions,
  buttonRefs,
  btnIndex,
  imageInfo,
  isFirstImage = true,
  isPerImages,
  isDesktop,
}: {
  alignmentOptions: string;
  buttonRefs: any;
  btnIndex: number;
  imageInfo: DoubleImage;
  isFirstImage?: boolean;
  isDesktop: boolean;
  isPerImages: boolean;
}) {
  const { altText, href, mobileSrc, src, title } = imageInfo;

  const renderImage = () => {
    const alt = altText || title || '';
    const image = (
      <>
        <Image alt={alt} className="tw-object-cover tw-hidden large:tw-block" src={src} fill />
        <Image alt={alt} className="tw-object-cover tw-block large:tw-hidden" src={mobileSrc} fill />
      </>
    );
    if (href) {
      return (
        <a href={href} className="tw-block tw-w-full tw-object-cover">
          {image}
        </a>
      );
    }
    return image;
  };
  return (
    <div className="tw-relative tw-w-full large:tw-w-6/12 tw-h-full tw-aspect-[6/5] lg:tw-aspect-auto">
      {renderImage()}
      {isPerImages && (
        <ImagesText
          alignmentOptions={alignmentOptions}
          btnIndex={btnIndex}
          imageInfo={imageInfo}
          isDesktop={isDesktop}
          isFirstImage={isFirstImage}
          buttonRefs={buttonRefs}
        />
      )}
    </div>
  );
}

export default function DoubleImageBanner({
  alignmentOptions,
  anchorLinkID,
  image1,
  image2,
  margin,
  outlineMargin,
  textPosition,
}: DoubleImageBannerProps) {
  const buttonRefs = useRef<any[]>([]);
  const containerRef = useRef<any>(null);
  useEffect(() => {
    if (buttonRefs.current.length < 2) return;
    let maxWidth = buttonRefs.current[0].offsetWidth;
    if (buttonRefs.current[1].offsetWidth > maxWidth) {
      maxWidth = buttonRefs.current[1].offsetWidth;
      buttonRefs.current[0].style.width = `${maxWidth}px`;
    } else {
      buttonRefs.current[1].style.width = `${maxWidth}px`;
    }
  }, [buttonRefs]);

  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isPerImages = textPosition === 'perImages';

  return (
    <div
      className={beTwMerge(
        'tw-flex tw-flex-col tw-justify-center tw-items-start tw-w-full',
        getComponentMargin(margin),
        getComponentOutlineMargin(outlineMargin)
      )}
      {...getAnchorId(anchorLinkID)}
    >
      <div
        className="tw-relative tw-flex tw-items-start tw-w-full tw-flex-col large:tw-flex-row lg:tw-h-[31.25vw] tw-aspect-auto"
        ref={containerRef}
      >
        {image1 && (
          <HeroBanner
            alignmentOptions={alignmentOptions}
            buttonRefs={buttonRefs}
            btnIndex={0}
            imageInfo={image1}
            isDesktop={isDesktop}
            isPerImages={isPerImages}
          />
        )}
        {image2 && (
          <HeroBanner
            alignmentOptions={alignmentOptions}
            buttonRefs={buttonRefs}
            btnIndex={1}
            imageInfo={image2}
            isDesktop={isDesktop}
            isPerImages={isPerImages}
            isFirstImage={false}
          />
        )}
        {!isPerImages && image1 && (
          <ImagesText
            alignmentOptions={alignmentOptions}
            buttonRefs={buttonRefs}
            imageInfo={image1}
            isDesktop={isDesktop}
            slicedNumber={2}
          />
        )}
      </div>
    </div>
  );
}
